import { Button, Container, Chip, Tooltip } from '@mui/material'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Datagrid,
  FunctionField,
  List,
  Pagination,
  TextField,
  useListContext,
  useListController,
  useTranslate,
} from 'react-admin'
import { CardCommon } from 'src/components/uiElements'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import { Download } from '@mui/icons-material'
import * as XLSX from 'xlsx'
import { getConciliationTypeInfo } from './logic/conciliationTypeHelper'
import {
  SupplierConciliationFilters,
  PostFilterFormSupplierConciliation,
} from './logic/SupplierConciliationFilter'
import { clientServices } from 'src/services/auth/client/client.service'

const CustomExportButton = ({ clientsByHash = {} }) => {
  const translate = useTranslate()
  const controller = useListController()

  const sinTildes = (texto) =>
    texto ? texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : ''

  const handleExport = () => {
    const rows = controller.data?.map((row) => {
      const hashClient = row?.client_relation?.hash_client || row?.hash_client
      const client = hashClient ? clientsByHash[hashClient] : null
      const clientName = client?.long_name || row?.client_relation?.long_name || '-'

      return {
        ID: row.id,
        [sinTildes(translate('ra.field.name'))]: row?.name || '-',
        [sinTildes(translate('ra.field.client'))]: clientName,
        [sinTildes(translate('ra.field.supplier'))]:
          row?.supplier_relation?.name || row?.supplier_name || '-',
        [sinTildes(translate('ra.field.type'))]:
          getConciliationTypeInfo(row?.conciliation_type, translate).name || '-',
        [sinTildes(translate('ra.field.created_at'))]: row?.created_at
          ? new Date(row.created_at).toLocaleDateString('es-AR')
          : '-',
        [sinTildes(translate('ra.field.currency'))]:
          row?.currency_relation?.code ||
          row?.currency_relation?.short_name ||
          '-',
        [sinTildes(translate('ra.field.total_records'))]:
          row?.stats?.TOTAL_RECORDS ??
          row?.stats?.total_records ??
          row?.stats?.total_items ??
          0,
        [sinTildes(translate('ra.field.reconciled'))]:
          row?.stats?.RECONCILED ??
          row?.stats?.reconciled ??
          row?.stats?.reconciled_items ??
          0,
        [sinTildes(translate('ra.field.not_found'))]:
          row?.stats?.NOT_FOUND ??
          row?.stats?.not_found ??
          row?.stats?.not_found_items ??
          0,
        [sinTildes(translate('ra.field.with_differences'))]:
          row?.stats?.AMOUNT_MISMATCH ??
          row?.stats?.amount_mismatch ??
          row?.stats?.amount_mismatch_items ??
          0,
      }
    })

    if (!rows || !rows.length) {
      return
    }

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Datos')
    XLSX.writeFile(wb, 'conciliaciones.xlsx')
  }

  return (
    <Button
      onClick={handleExport}
      startIcon={<Download fontSize='small' />}
      style={{ textTransform: 'none' }}
    >
      {translate('ra.action.export')}
    </Button>
  )
}

export const SupplierConciliationList = (props) => {
  const translate = useTranslate()
  const { isFetching, page, filterValues } = useListContext()
  const prevPageRef = useRef()
  const prevFiltersRef = useRef(filterValues)
  const [pageChanged, setPageChanged] = useState(false)
  const [clientsByHash, setClientsByHash] = useState({})

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await clientServices
          .getByAll({ limit: 999 })
          .catch(() => clientServices.getByAll())
        const clientList = Array.isArray(response)
          ? response
          : (Array.isArray(response?.data) ? response.data : [])

        const map = {}
        clientList.forEach((c) => {
          if (c?.hash_client) {
            map[c.hash_client] = c
          }
        })
        setClientsByHash(map)
      } catch (error) {
        console.error('Error fetching clients for supplier conciliation list:', error)
      }
    }

    fetchClients()
  }, [])

  const hasPermissions = () => {
    return (
      hasPermissionFunction('supplier-conciliation', 'show') ||
      hasPermissionFunction('supplier-conciliation', 'edit') ||
      hasPermissionFunction('supplier-conciliation', 'delete')
    )
  }

  const allFilters = [
    { source: 'name', name: 'ra.field.name' },
    { source: 'created_at', name: 'ra.field.created_at' },
    { source: 'supplier_id', name: 'ra.field.supplier' },
    { source: 'general_status', name: 'ra.field.general_status' },
  ]

  useEffect(() => {
    if (prevPageRef.current !== page && isFetching) {
      prevPageRef.current = page
      setPageChanged(true)
    }
    if (prevFiltersRef.current !== filterValues && isFetching) {
      prevFiltersRef.current = filterValues
      setPageChanged(true)
    } else {
      setPageChanged(false)
    }
  }, [isFetching, page, filterValues])

  const renderExportButton = useCallback(
    () => <CustomExportButton clientsByHash={clientsByHash} />,
    [clientsByHash]
  )

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.supplier-conciliation')}>
        <List
          {...props}
          resource='supplier-conciliation'
          sort={{ field: 'id', order: 'DESC' }}
          perPage={10}
          pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100]} />}
          component='div'
          actions={
            <ListViewTopToolbar
              customFilter
              FilterFormComponent={PostFilterFormSupplierConciliation}
              FilterButtonComponent={SupplierConciliationFilters}
              listFilters={allFilters}
              CustomExportButton={renderExportButton}
              exporterDisable={true}
            />
          }
        >
          <Datagrid bulkActionButtons={false} isLoading={pageChanged}>
            <TextField source='id' label='ID' />
            <FunctionField
              label={translate('ra.field.name')}
              render={(record) => record?.name || '-'}
            />
            <FunctionField
              label={translate('ra.field.client')}
              render={(record) => {
                const hashClient = record?.client_relation?.hash_client || record?.hash_client
                if (!hashClient) return '-'
                return (
                  clientsByHash[hashClient]?.long_name ||
                  record?.client_relation?.long_name ||
                  '-'
                )
              }}
            />
            <FunctionField
              label={translate('ra.field.supplier')}
              render={(record) =>
                record?.supplier_relation?.name || record?.supplier_name || '-'
              }
            />
            <FunctionField
              label={translate('ra.field.type')}
              render={(record) => {
                const { name, icon } = getConciliationTypeInfo(record?.conciliation_type, translate)

                if (!icon && (!name || name === '-')) return '-'

                return (
                  <Tooltip title={name}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {icon || name}
                    </div>
                  </Tooltip>
                )
              }}
            />
            <FunctionField
              label={translate('ra.field.created_at')}
              render={(record) =>
                record?.created_at
                  ? new Date(record.created_at).toLocaleDateString('es-AR')
                  : '-'
              }
            />
            <FunctionField
              label={translate('ra.field.currency')}
              render={(record) =>
                record?.currency_relation?.code ||
                record?.currency_relation?.short_name ||
                '-'
              }
            />
            <FunctionField
              label={translate('ra.field.general_status') || 'Estado'}
              render={(record) => {
                const status = record?.general_status || record?.status
                let text = '-'
                let color = '#757575'
                let bgColor = '#f5f5f5'

                if (status === 'OPEN') {
                  text = translate('ra.status.open_fem') || translate('ra.status.open')
                  color = '#0288d1'
                  bgColor = '#e0f7fa'
                } else if (status === 'CLOSED') {
                  text = translate('ra.status.closed_fem') || translate('ra.status.closed')
                  color = '#d32f2f'
                  bgColor = '#ffebee'
                } else if (status === 'PROCESSING') {
                  text = translate('ra.status.processing')
                  color = '#ed6c02'
                  bgColor = '#fff3e0'
                } else if (status === 'DELETED') {
                  text = translate('ra.status.deleted_fem') || translate('ra.status.deleted')
                  color = '#757575'
                  bgColor = '#eeeeee'
                } else if (status === 'COMPLETED') {
                  text = translate('ra.status.completed_fem') || translate('ra.status.completed')
                  color = '#2e7d32'
                  bgColor = '#e8f5e9'
                } else if (status) {
                  text = status
                }

                return (
                  <Chip
                    label={text}
                    size='small'
                    sx={{
                      bgcolor: bgColor,
                      color: color,
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      border: `1px solid ${color}40`,
                    }}
                  />
                )
              }}
            />
            <FunctionField
              label={translate('ra.field.total_records')}
              render={(record) =>
                record?.stats?.TOTAL_RECORDS ??
                record?.stats?.total_records ??
                record?.stats?.total_items ??
                0
              }
            />
            <FunctionField
              label={translate('ra.field.reconciled')}
              render={(record) => {
                const val =
                  record?.stats?.RECONCILED ??
                  record?.stats?.reconciled ??
                  record?.stats?.reconciled_items ??
                  0
                const isGreen = val > 0
                return (
                  <span
                    style={{
                      color: isGreen ? '#2e7d32' : 'inherit',
                      fontWeight: isGreen ? 'bold' : 'normal',
                    }}
                  >
                    {val}
                  </span>
                )
              }}
            />
            <FunctionField
              label={translate('ra.field.not_found')}
              render={(record) => {
                const val =
                  record?.stats?.NOT_FOUND ??
                  record?.stats?.not_found ??
                  record?.stats?.not_found_items ??
                  0
                const isRed = val > 0
                return (
                  <span
                    style={{
                      color: isRed ? '#d32f2f' : 'inherit',
                      fontWeight: isRed ? 'bold' : 'normal',
                    }}
                  >
                    {val}
                  </span>
                )
              }}
            />
            <FunctionField
              label={translate('ra.field.with_differences')}
              render={(record) => {
                const val =
                  record?.stats?.AMOUNT_MISMATCH ??
                  record?.stats?.amount_mismatch ??
                  record?.stats?.amount_mismatch_items ??
                  0
                const isOrange = val > 0
                return (
                  <span
                    style={{
                      color: isOrange ? '#ed6c02' : '#2e7d32',
                      fontWeight: 'bold',
                    }}
                  >
                    {val}
                  </span>
                )
              }}
            />
            {hasPermissions() && (
              <ListViewActionsButtons
                label={translate('ra.action.actions')}
                genderElement='femenine'
                disableRevision
              />
            )}
          </Datagrid>
        </List>
      </CardCommon>
    </Container>
  )
}
